/**
 * viewConfig — TV-RV-05
 *
 * Declarative table that drives:
 *   - The phase ribbon in AppShell (which tabs are visible)
 *   - The post-login redirect home route
 *   - Feature flags consumed by components (canSeeClinical, canJoinVisit, visitMode)
 *   - Route guards in RequireRole (clinical routes redirect non-providers to their home)
 *
 * uiMode ('full' | 'lite') remains orthogonal — AppShell intersects cfg.phases
 * with LITE_PHASE_KEYS when isLite is true.
 *
 * Front-desk feature SURFACES (queue check-in actions, monitor-join UI, scheduling
 * form, billing read-view) are Slice 2 / TV-RV-08..11. The config rows below are
 * complete for Slice 1 so all three roles route and gate correctly.
 */

/**
 * @typedef {Object} ViewConfig
 * @property {string}   home          - default route after login
 * @property {string[]} phases        - ordered phase keys visible in the ribbon
 * @property {boolean}  canSeeClinical - whether clinical chart/notes panels show
 * @property {boolean}  canJoinVisit  - whether the user can enter the video room
 * @property {string|null} visitMode  - 'clinical' | 'monitor' | 'patient' | null
 */

/** @type {Record<string, ViewConfig>} */
export const VIEW_CONFIG = {
  'front-desk': {
    home: '/queue',
    // Slice 2 will add the /schedule route when the scheduling surface is built.
    phases: ['queue', 'schedule', 'visit', 'billing', 'summary'],
    canSeeClinical: false,
    canJoinVisit: true,
    visitMode: 'monitor',
  },
  'provider': {
    home: '/queue',
    phases: ['queue', 'visit', 'assessment', 'careplan', 'ai-docs', 'ai-agent', 'billing', 'summary'],
    canSeeClinical: true,
    canJoinVisit: true,
    visitMode: 'clinical',
  },
  'patient': {
    home: '/waiting-room',
    phases: ['waiting-room', 'visit', 'summary'],
    canSeeClinical: false,
    canJoinVisit: true,
    visitMode: 'patient',
  },
  'admin': {
    home: '/queue',
    phases: ['queue', 'summary'],
    canSeeClinical: false,
    canJoinVisit: false,
    visitMode: null,
  },
};

/**
 * Convenience helper — returns VIEW_CONFIG[role] with a safe patient fallback.
 * @param {string} role
 * @returns {ViewConfig}
 */
export function getViewConfig(role) {
  return VIEW_CONFIG[role] ?? VIEW_CONFIG['patient'];
}
